/**
 * DimensionScoreBar - Small reusable horizontal bar chart for showing
 * a single 0-100 dimension score with color coding.
 */
import React from 'react';

interface DimensionScoreBarProps {
  label: string;
  // Accept null for "dimension not applicable" (e.g. instrument dim
  // on Company targets — it gets dropped from the weighted total and
  // dimension_scores[key] comes back as null). Rendered as an em-dash.
  score?: number | null;
}

const colorFor = (score: number): string => {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-yellow-500';
  if (score >= 40) return 'bg-orange-500';
  return 'bg-red-500';
};

const DimensionScoreBar: React.FC<DimensionScoreBarProps> = ({ label, score }) => {
  const value = typeof score === 'number' ? Math.max(0, Math.min(100, score)) : 0;
  const hasScore = typeof score === 'number';

  return (
    <div>
      <div className="flex items-center justify-between text-xs text-gray-700 mb-1">
        <span className="font-medium">{label}</span>
        <span className="text-gray-600">
          {hasScore ? `${value.toFixed(0)} / 100` : '—'}
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div
          className={`${colorFor(value)} h-2 rounded-full transition-all`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
};

export default DimensionScoreBar;
